import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 10000,
  headers: {
    ...(process.env.REACT_APP_API_KEY ? { 'x-api-key': process.env.REACT_APP_API_KEY } : {}),
  },
});

// Error types
const ErrorType = {
  AUTHENTICATION: 'AUTHENTICATION', // 401 (Unauthorized)
  VALIDATION: 'VALIDATION', // 400 (Bad Request)
  NETWORK: 'NETWORK', // 0 (No Response)
  SERVER: 'SERVER', // 500 (Internal Server Error)
  UNKNOWN: 'UNKNOWN' // Anything else
};

// Error handler helper
const handleError = (error) => {
  // Network error
  if (!error.response) {
    return {
      type: ErrorType.NETWORK,
      message: 'Unable to connect to the server. Please check your internet connection.',
      status: 0,
      originalError: error
    };
  }

  // Server returned an error response
  const { status, data } = error.response;

  // If the backend provides a specific error code, use it
  // THIS WAS ADDED TO HANDLE EXPIRED VERIFICATION LINK TOKEN BUT IT BREAKS OTHER ERROR MESSAGES, NEED TO CONSOLIDATE!!!!
  
  //if (data && data.code) {
  //  return {
  //    type: data.code, // Use the specific error code from backend
  //    message: data.error || 'An error occurred.',
  //    status,
  //    originalError: error
  //  };
  //}

  // Fallback to status-based error types
  switch (status) {
    case 400:
      return {
        type: ErrorType.VALIDATION,
        message: data.error || 'Invalid input. Please check your data.',
        status,
        originalError: error
      };
    case 401:
      return {
        type: ErrorType.AUTHENTICATION,
        message: data.error || 'Authentication failed.',
        status,
        originalError: error
      };
    case 423:
      return {
        type: ErrorType.AUTHENTICATION,
        message: data.error || 'Account is locked.',
        status,
        originalError: error
      };
    case 409:
      return {
        type: ErrorType.VALIDATION,
        message: data.error || 'This operation cannot be completed.',
        status,
        originalError: error
      };
    case 500:
      return {
        type: ErrorType.SERVER,
        message: 'An unexpected error occurred. Please try again later.',
        status,
        originalError: error
      };
    default:
      return {
        type: ErrorType.UNKNOWN,
        message: 'An unexpected error occurred. Please try again.',
        status,
        originalError: error
      };
  }
};

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const handledError = handleError(error);
    
    // Only log non-auth errors
    if (handledError.type !== ErrorType.AUTHENTICATION) {
      console.error('API Error:', {
        type: handledError.type,
        status: handledError.status,
        message: handledError.message,
        originalError: handledError.originalError
      });
    }
    
    return Promise.reject(handledError);
  }
);

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Function to log in a user
export const loginUser = async (userData) => {
  try {
    const response = await api.post('/login', userData);
    return response.data.token;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to refresh token. Pass updateLastLogin when restoring a browser session on app load.
export const refreshToken = async ({ updateLastLogin = false } = {}) => {
  try {
    const response = await api.post('/refreshToken', updateLastLogin ? { updateLastLogin: true } : {});
    return response.data.token;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to request password reset 
export const resetPassword = async ({ email }) => {
  try {
    const response = await api.post('/resetPassword', { email });
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to validate reset password Url 
export const validateResetPasswordUrl = async ({ token, uuid }) => {
  try {
    const response = await api.get('/resetPassword', {
      params: { token, uuid } // Send token and uuid as query parameters
    });  
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to register a user
export const registerUser = async ({ firstName, lastName, email, password }) => {
  try {
    const response = await api.post('/register', {
      firstName,
      lastName,
      email,
      password,
    });
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to verify a user
export const verifyUser = async ({ token, uuid }) => {
  try {
    const response = await api.get('/verify', {
      params: { token, uuid } // Send token and uuid as query parameters
    });
    return response.data; // Return the response data
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to resend verification email
export const resendVerification = async ({ email }) => {
  try {
    const response = await api.post('/resendVerification', { email });
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to create a booking
export const createBooking = async (bookingData) => {
  try {
    console.log('CREATE_BOOKINGS(S):', bookingData);
    const response = await api.post('/createBooking', bookingData);
    console.log('CREATE_BOOKINGS(S) RESPONSE:', response.data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to update a booking
export const updateBooking = async (bookingData) => {
  try {
    console.log('UPDATE_BOOKING(S):', bookingData);
    const response = await api.put('/updateBooking', bookingData);
    console.log('UPDATE_BOOKING(S) RESPONSE:', response.data);

    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to get bookings by user
export const getBookingsByUser = async () => {
  try {
    console.log('Calling API getBookingsByUser');

    const response = await api.get('/userBookings');
    console.log('User Bookings: ', response.data);
    const responseData = response.data;
    const bookings = Array.isArray(responseData)
      ? responseData
      : (Array.isArray(responseData?.bookings) ? responseData.bookings : []);
    const normalizedBookings = Array.isArray(bookings) ? [...bookings] : [];
    if (responseData?.token) {
      normalizedBookings._token = responseData.token;
    }
    return normalizedBookings;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Helper to convert a Date object to YYYY-MM-DD in local time
const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Function to get bookings in a date range
export const getBookingsInRange = async (startDate, endDate) => {
  try {
    const start = toLocalDateString(new Date(startDate));
    const end = toLocalDateString(new Date(endDate));

    console.log('Calling API getBookingsInRange for date range:', start, end);

    const response = await api.get('/calendarBookings', {
      params: { startDate: start, endDate: end }
    });

    console.log('Bookings: ', response.data);
    const responseData = response.data;
    const bookings = Array.isArray(responseData)
      ? responseData
      : (Array.isArray(responseData?.bookings) ? responseData.bookings : []);
    const normalizedBookings = Array.isArray(bookings) ? [...bookings] : [];
    if (responseData?.token) {
      normalizedBookings._token = responseData.token;
    }
    return normalizedBookings;
  } catch (error) {
    throw error;
  }
};

// Function to get all bookings (for analytics)
export const getAllBookings = async () => {
  try {
    console.log('Calling API getAllBookings for all time');

    const response = await api.get('/allBookings');

    console.log('All Bookings: ', response.data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to delete a booking
export const deleteBooking = async (bookingData) => {
  try {
    console.log('DELETE_BOOKING(S):', bookingData);

    const requestData = {
      bookingDate: bookingData.bookingDate,
      court: bookingData.court,
      startTime: bookingData.startTime,
      bookingId: bookingData.bookingId // This will be excluded if the user is only deletign an individual booking and not all bookings grouped with the same bookingId
    };

    // Add bookingKeys if provided (for performance optimization)
    if (bookingData.bookingKeys) {
      requestData.bookingKeys = bookingData.bookingKeys;
    }

    const response = await api.delete('/deleteBooking', {
      data: requestData
    });
    console.log('DELETE_BOOKING(S) RESPONSE:', response.data);

    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to get all courts
export const getCourts = async () => {
  try {
    const response = await api.get('/courts');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to update all provided courts
export const updateCourts = async (courtData) => {
  try {
    console.log('*** Updating courts to:', courtData)
    const response = await api.post('/courts', courtData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to get all booking categories
export const getBookingCategories = async () => {
  try {
    const response = await api.get('/bookingCategories');
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to update all booking categories
export const updateBookingCategories = async (categoriesData) => {
  try {
    console.log('*** Updating categories to:', categoriesData)
    const response = await api.post('/bookingCategories', categoriesData);
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to get all booking options
export const getBookingOptions = async () => {
  try {
    const response = await api.get('/bookingOptions');
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to update all booking options
export const updateBookingOptions = async (optionsData) => {
  try {
    console.log('*** Updating booking options to:', optionsData)
    const response = await api.post('/bookingOptions', optionsData);
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to get club details
export const getClubDetails = async () => {
  try {
    const response = await api.get('/clubDetails');
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Club terms (fetched from club-details, excludes termsHtml from getClubDetails)
export const getClubTerms = async () => {
  try {
    const response = await api.get('/clubTerms');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to update club details
export const updateClubDetails = async (clubDetails) => {
  try {
    console.log('*** Updating club details to:', clubDetails)
    const response = await api.put('/clubDetails', clubDetails);
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

export const testShellySwitch = async ({ serverUrl, apiKey, deviceId, court }) => {
  try {
    const response = await api.post('/testShellySwitch', {
      serverUrl,
      apiKey,
      deviceId,
      court,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const operateLights = async ({ court, bookingDate, endTime, courtStartTime, action = 'on' }) => {
  try {
    const response = await api.post('/operateLights', {
      court,
      bookingDate,
      endTime,
      courtStartTime,
      action,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Booking-bound access code (or future Shelly pulse). Mirrors operateLights identity fields. */
export const grantAccess = async ({ bookingDate, endTime, courtStartTime } = {}) => {
  const response = await api.post('/grantAccess', {
    bookingDate,
    endTime,
    courtStartTime,
  });
  return response.data;
};

// Function to update booking status
export const updateBookingStatus = async (bookingData) => {
  try {
    console.log('UPDATE_BOOKING_STATUS:', bookingData);
    const response = await api.post('/updateBookingStatus', bookingData);
    console.log('UPDATE_BOOKING_STATUS RESPONSE:', response.data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to create Yoco checkout session
export const createYocoCheckout = async (checkoutData) => {
  try {
    console.log('CREATE_YOCO_CHECKOUT:', checkoutData);
    const response = await api.post('/createYocoCheckout', checkoutData);
    console.log('CREATE_YOCO_CHECKOUT RESPONSE:', response.data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to create iKhokha checkout session
export const createIkhokhaCheckout = async (checkoutData) => {
  try {
    console.log('CREATE_IKHOKHA_CHECKOUT:', checkoutData);
    const response = await api.post('/createIkhokhaCheckout', checkoutData);
    console.log('CREATE_IKHOKHA_CHECKOUT RESPONSE:', response.data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to create Zapper Hosted checkout session
export const createZapperHostedCheckout = async (checkoutData) => {
  try {
    console.log('CREATE_ZAPPER_HOSTED_CHECKOUT:', checkoutData);
    const response = await api.post('/createZapperHostedCheckout', checkoutData);
    console.log('CREATE_ZAPPER_HOSTED_CHECKOUT RESPONSE:', response.data);
    return response.data;
  } catch (error) {
    throw error;
  }
};


// Function to get all user types
export const getUserTypes = async () => {
  try {
    const response = await api.get('/userTypes');

    console.log("******* getUserTypes repsonse.data", response.data);
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to update all user types
export const updateUserTypes = async (userTypes) => {
  try {
    const response = await api.post('/userTypes', userTypes);
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to get all users
export const getAllUsers = async () => {
  try {
    const response = await api.get('/users');

    console.log("******* getUsers repsonse.data", response.data);
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to update the provided users
export const updateUsers = async (users) => {
  try {
    const response = await api.post('/users', users);
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to delete a user
export const deleteUser = async (uuid) => {
  try {
    console.log('DELETE_USER:', uuid);
    const response = await api.delete('/deleteUser', { data: { uuid } });
    console.log('DELETE_USER RESPONSE:', response.data);
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to change password
export const changePassword = async ({ password, token }) => {
  
  try {
    // Add reset token to localStorage for api interceptor to include as Authorization header
    localStorage.setItem('token', token);
    const response = await api.post('/changePassword', { password });
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to get all email templates
export const getEmailTemplates = async () => {
  try {
    const response = await api.get('/emailTemplates');
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to update all email templates
export const updateEmailTemplates = async (templates) => {
  try {
    const response = await api.post('/emailTemplates', templates);
    return response.data;
  } catch (error) {
    throw error; // Error already handled by interceptor
  }
};

// Function to renew membership
export const renewMembership = async (reference, targetUserTypeId = null) => {
  try {
    const response = await api.post('/renewMembership', {
      reference,
      ...(targetUserTypeId ? { targetUserTypeId } : {})
    });
    return response.data;
  } catch (error) {
    throw error; 
  }
};

export const getProfileMetaData = async (uuid) => {
  try {
    const response = await api.get('/profileMetaData', {
      params: uuid ? { uuid } : undefined
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const saveProfileMetaData = async (metaData, uuid, options = {}) => {
  try {
    const body = {};

    if (options.cancelApplication) {
      body.cancelApplication = true;
    } else {
      body.metaData = metaData || {};
    }

    if (Object.prototype.hasOwnProperty.call(options, 'memberStatus')) {
      body.memberStatus = options.memberStatus;
    }

    const response = await api.put('/profileMetaData', body, {
      params: uuid ? { uuid } : undefined
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to purchase credits
export const purchaseCredits = async (reference, creditAmount) => {
  try {
    const response = await api.post('/purchaseCredits', { reference, creditAmount });
    return response.data;
  } catch (error) {
    throw error; 
  }
};

export const startPendingPayment = async (payload) => {
  try {
    const response = await api.post('/startPendingPayment', payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export { ErrorType };
export default api;
